import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';
import { trackCalendlyConversion } from './GoogleTagLoader';
export function CaseStudiesSection() {
  const caseStudies = [{
    agency: 'Clearstep Health',
    headline: 'Turned one conference into 5.2x on spend',
    description: 'A 40-person sales team knew their buyers went to VetForward but had no field marketer to work it. Turnout priced the event in pipeline, filled a side dinner with 38 practice owners, and booked the meetings on the floor.',
    metrics: [{
      value: '5.2x',
      label: 'ROI on Spend'
    }, {
      value: '14',
      label: 'Meetings Booked'
    }],
    link: 'https://www.phoxevents.com/about',
    initial: 'C'
  }, {
    agency: 'Northwind Data',
    headline: 'Beat their outbound cost per meeting with events',
    description: 'Every SDR dollar was buying a more expensive meeting each quarter. Turnout ran three events against the same ICP and delivered meetings at a lower blended cost, with a forecast the CRO could take to the board.',
    metrics: [{
      value: '$310',
      label: 'Cost Per Meeting'
    }, {
      value: '3',
      label: 'Events/Quarter'
    }],
    link: 'https://buflusevents.com/',
    initial: 'N'
  }, {
    agency: 'Meridian Ops',
    headline: 'Forecasts event pipeline like any other channel',
    description: 'Events used to be an unbudgetable line item nobody owned. Now Meridian reviews spend, forecast, and actuals in a 30-minute standup, and events sit next to outbound in the same pipeline model.',
    metrics: [{
      value: '$418K',
      label: 'Pipeline Influenced'
    }, {
      value: '30 min',
      label: 'Weekly Time Spent'
    }],
    link: 'https://ondotexperience.com/',
    initial: 'M'
  }];
  const containerVariants = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: [0.25, 0.46, 0.45, 0.94]
      }
    }
  };
  return <section id="case-studies" className="py-24 bg-gray-50" data-id="element-40" data-mp="2hm7kbk62nptl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-41" data-mp="2co61n0ck5sin">
        <AnimatedSection data-id="element-42" data-mp="27q4gm9vknj4j">
          <div className="text-center mb-16" data-id="element-43" data-mp="27pcpbb90c7k2">
            <p className="text-sm font-semibold text-indigo-600 tracking-wide uppercase mb-3" data-id="element-44" data-mp="27s78z0k75ocf">
              Real Results
            </p>
            <h2 className="text-3xl font-extrabold text-gray-900 sm:text-4xl" data-id="element-45" data-mp="5z9lzld3ppze">
              Sales Teams Running Events as a Channel
            </h2>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 mx-auto" data-id="element-46" data-mp="1xxqx0bgkcjt">
              Real programs, priced in pipeline.
            </p>
          </div>
        </AnimatedSection>

        <motion.div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.15
      }} data-id="element-47" data-mp="1osktxr1ycnf2">
          {caseStudies.map((study, index) => <motion.a key={index} href={study.link} target="_blank" rel="noopener noreferrer" variants={cardVariants} whileHover={{
          y: -4
        }} className="group relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col h-full cursor-pointer" data-id="element-48" data-mp="10y01qgtq3071" data-mpi={index}>
              {/* Top accent line */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-indigo-600 transform origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-300" data-id="element-49" data-mp="2qcy1dxvz4b72" />

              <div className="p-8 flex-1 flex flex-col relative z-10" data-id="element-50" data-mp="2jw5zdhql9k2e">
                {/* Header: Logo + Brand Name */}
                <div className="mb-6" data-id="element-51" data-mp="2xs9oiwmlv280">
                  <span className="text-xs font-bold text-gray-400 uppercase tracking-widest" data-id="element-52" data-mp="2ts9ave7byv20">
                    {study.agency}
                  </span>
                </div>

                {/* Narrative Headline */}
                <h3 className="text-xl font-bold text-gray-900 mb-6 leading-tight group-hover:text-indigo-600 transition-colors duration-200" data-id="element-53" data-mp="32sfnyyz49t0n">
                  {study.headline}
                </h3>

                {/* Metrics Row */}
                <div className="grid grid-cols-2 gap-4 mb-6 pt-6 border-t border-gray-100 relative" data-id="element-54" data-mp="3cld8fano5s1w">
                  {/* Watermark behind metrics */}
                  <div className="absolute -right-2 -bottom-4 text-9xl font-black text-gray-50 opacity-[0.03] select-none pointer-events-none font-serif leading-none z-0" data-id="element-55" data-mp="3lkqahi9grecl">
                    {study.initial}
                  </div>

                  {study.metrics.map((metric, i) => <div key={i} className="relative z-10" data-id="element-56" data-mp="7jl5su7t5o" data-mpi={i}>
                      <p className="text-3xl font-bold text-indigo-600 tracking-tight" data-id="element-57" data-mp="1r2z59sikbo">
                        {metric.value}
                      </p>
                      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mt-1" data-id="element-58" data-mp="3me7pece1xwas">
                        {metric.label}
                      </p>
                    </div>)}
                </div>

                {/* Description */}
                <p className="text-gray-600 text-sm leading-relaxed mb-6 flex-1" data-id="element-59" data-mp="3hfwfk1nrblsk">
                  {study.description}
                </p>

                {/* Read More Link */}
                <div className="flex items-center text-indigo-600 font-semibold text-sm group-hover:gap-2 transition-all duration-200 mt-auto" data-id="element-60" data-mp="33ir23glprkna">
                  <span data-id="element-61" data-mp="1dzj67qxz9srh">Read full story</span>
                  <ArrowUpRight className="h-4 w-4 ml-1 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform duration-200" data-id="element-62" data-mp="1e1cc340ro8yf" />
                </div>
              </div>
            </motion.a>)}
        </motion.div>

        <AnimatedSection delay={0.5} data-id="element-63" data-mp="108nqpvsz4dex">
          <div className="mt-16 text-center" data-id="element-64" data-mp="109pf11z02wel">
            <a href="https://calendly.com/dan-at-turnout/turnout-general-30-min-call" target="_blank" rel="noopener noreferrer" onClick={trackCalendlyConversion} className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-xl shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 transition-colors duration-300" data-id="element-65" data-mp="1wyn9sk6r2ubr">
              See How It Works
            </a>
          </div>
        </AnimatedSection>
      </div>
    </section>;
}